// components/ChainSwitcher.tsx
import { useSwitchChain, useChainId, useChains } from 'wagmi';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Check, Zap } from 'lucide-react';
import {
    TriggerButton,
    ChainInfo,
    ChainIcon,
    ChainName,
    LoadingSpinner,
    Dropdown,
    DropdownHeader,
    DropdownTitle,
    DropdownSubtitle,
    ChainList,
    ChainItem,
    ChainItemLeft,
    ChainItemIcon,
    ChainItemInfo,
    ChainItemName,
    ChainItemId,
    ChainItemRight,
    ActiveBadge,
    StatusDot,
    DropdownFooter,
    FooterText,
    LiveDot,
    Container,
} from './style';
import { Chain } from 'viem';
import DropdownIcon from '../SVG/DropdownIcon';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { spenTokenChange, tokenChange } from '@/store';
import { useDispatch } from 'react-redux';
import { MainTokenConfig } from '@/config';
import { TokenType } from '@/store/type';

export interface ChainWithIcon extends Chain {
    iconUrl?: string;
}

const chainColors: Record<number, string> = {
    1: '#627EEA',
    56: '#F0B90B',
    137: '#8247E5',
    8453: '#0052FF',
    10: '#FF0420',
    42161: '#2D374B',
    43114: '#E84142',
    196: '#6C3BCF',
    4663: '#00D4AA',
};

const PANEL_WIDTH = 320; // 与 style.ts 中 min(320px, ...) 保持一致
const VIEWPORT_PADDING = 12; // 面板与视口边缘的最小距离

interface ChainSwitcherProps {
    className?: string;
    onSwitch?: (chainId: number) => void;
}

export function ChainSwitcher({ className = '', onSwitch }: ChainSwitcherProps) {
    const { switchChain, isPending } = useSwitchChain();

    const wagmiChainId = useChainId();
    const availableChains = useChains();
    const [isOpen, setIsOpen] = useState(false);
    const [offsetX, setOffsetX] = useState(0);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const urlChainId = searchParams.get('chainId');
    const currentChainId = urlChainId ? parseInt(urlChainId, 10) : wagmiChainId;

    const currentChain = availableChains.find(
        (c) => c.id === currentChainId
    ) as ChainWithIcon | undefined;

    // 🆕 计算面板的偏移量，保证不超出视口
    const computeOffset = () => {
        if (!dropdownRef.current) return;
        const rect = dropdownRef.current.getBoundingClientRect();
        const panelWidth = Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_PADDING * 2);

        // 面板默认右对齐按钮：面板右边缘 = 按钮右边缘
        // 若面板左边缘 < 0，说明左边溢出，需要向右推
        const panelLeftIfRightAligned = rect.right - panelWidth;
        const overflowLeft = VIEWPORT_PADDING - panelLeftIfRightAligned;

        // 若面板右边缘 > 视口宽，说明右边溢出，需要向左推
        const overflowRight = rect.right + VIEWPORT_PADDING - window.innerWidth;

        let nextOffset = 0;
        if (overflowLeft > 0) {
            nextOffset = -overflowLeft; // right 为正 → 往左推
        } else if (overflowRight > 0) {
            nextOffset = overflowRight; // right 为负 → 往右推
        }
        setOffsetX(nextOffset);
    };

    useLayoutEffect(() => {
        if (isOpen) computeOffset();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleResize = () => computeOffset();
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
        };
    }, [isOpen]);

    // 点击外部关闭
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSwitch = async (chainId: number) => {
        const params = new URLSearchParams(searchParams.toString());
        params.set('chainId', String(chainId));

        const newPath = `${location.pathname}?${params.toString()}`;
        console.log('newPath::: ', newPath);
        navigate(newPath, { replace: true });
        const _mainTokenConfig = MainTokenConfig[chainId];
        dispatch(spenTokenChange(_mainTokenConfig[0]));
        const newTokens: TokenType = { address: "", symbol: "", decimals: 18 };
        dispatch(tokenChange(newTokens));
        if (chainId === currentChainId) {
            setIsOpen(false);
            return;
        }

        try {
            await switchChain({ chainId });
            onSwitch?.(chainId);
            setIsOpen(false);
        } catch (err) {
            console.error('Failed to switch chain:', err);
        }
    };

    const renderChainIcon = (chain: ChainWithIcon) => {
        if (chain.iconUrl) {
            return (
                <img
                    src={chain.iconUrl}
                    alt={chain.name}
                    style={{ borderRadius: '50%', objectFit: 'contain' }}
                    onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                    }}
                />
            );
        }

        const fallbackImages: Record<number, string> = {
            56: '/images/BNB.png',
            97: '/images/BNB.png',
            1: '/images/ETH.png',
            8453: '/images/BASE.png',
            42161: '/images/Arb.png',
            137: '/images/MATIC.png',
            5042: '/images/ARC.png',
        };

        const fallbackImage = fallbackImages[chain.id];
        if (fallbackImage) {
            return (
                <img
                    src={fallbackImage}
                    alt={chain.name}
                    style={{ borderRadius: '50%', objectFit: 'contain' }}
                />
            );
        }

        return '⛓️';
    };

    return (
        <Container ref={dropdownRef} $isOpen={isOpen} className={className}>
            <TriggerButton
                onClick={() => setIsOpen(!isOpen)}
                disabled={isPending}
                $isOpen={isOpen}
                $chainId={currentChainId}
            >
                <ChainInfo>
                    <ChainIcon $chainId={currentChainId}>
                        {currentChain ? renderChainIcon(currentChain) : ''}
                    </ChainIcon>
                    <ChainName>{currentChain?.name ?? 'Unknown'}</ChainName>
                </ChainInfo>
                {isPending ? <LoadingSpinner /> : <DropdownIcon $isOpen={isOpen} />}
            </TriggerButton>

            {isOpen && (
                <Dropdown $offsetX={offsetX}>
                    <DropdownHeader>
                        <DropdownTitle>Switch Network</DropdownTitle>
                        <DropdownSubtitle>Select a network to connect</DropdownSubtitle>
                    </DropdownHeader>

                    <ChainList>
                        {availableChains.map((chain) => {
                            const chainWithIcon = chain as ChainWithIcon;
                            const isActive = chain.id === currentChainId;
                            const color = chainColors[chain.id] || '#6B7280';

                            return (
                                <ChainItem
                                    key={chain.id}
                                    onClick={() => handleSwitch(chain.id)}
                                    disabled={isPending || isActive}
                                    $isActive={isActive}
                                    $chainId={chain.id}
                                >
                                    <ChainItemLeft>
                                        <ChainItemIcon $chainId={chain.id}>
                                            {renderChainIcon(chainWithIcon)}
                                        </ChainItemIcon>
                                        <ChainItemInfo>
                                            <ChainItemName $isActive={isActive}>
                                                {chain.name}
                                            </ChainItemName>
                                            <ChainItemId>Chain ID: {chain.id}</ChainItemId>
                                        </ChainItemInfo>
                                    </ChainItemLeft>
                                    <ChainItemRight>
                                        {isActive ? (
                                            <ActiveBadge>
                                                <Check size={12} />
                                                Connected
                                            </ActiveBadge>
                                        ) : (
                                            <>
                                                {isPending && (
                                                    <LoadingSpinner
                                                        style={{ width: 14, height: 14 }}
                                                    />
                                                )}
                                                <StatusDot $color={color} />
                                            </>
                                        )}
                                    </ChainItemRight>
                                </ChainItem>
                            );
                        })}
                    </ChainList>

                    <DropdownFooter>
                        <FooterText>
                            <LiveDot />
                            {availableChains.length} networks available
                        </FooterText>
                        <FooterText>
                            <Zap size={12} />
                            {availableChains.filter((c) => c.id === currentChainId).length > 0
                                ? 'Connected'
                                : 'Ready'}
                        </FooterText>
                    </DropdownFooter>
                </Dropdown>
            )}
        </Container>
    );
}